import {computed, reactive, ref} from "@vue/composition-api";
import {
    BuilderFormParameters,
    BuilderMaximizers,
    BuilderParameterInterface,
    BuilderParametersByType, BuilderStats,
    BuilderTemplateInterface,
    BuilderUrlInterface
} from "@tool/Builder/Builder.types";
import useBuilderLocalStorage from "@tool/Builder/helpers/useBuilderLocalStorage";
import {
    PARAMETER_TYPES,
    ParameterEntityInterface,
    ParameterType,
    ProjectBuilderSettingsInterface, ProjectEntityInterface,
    ShorturlEntityInterface,
    TemplateEntityInterface,
    UrlEntityInterface
} from "@tool/tool.types";
import useAxios from "@tool/Helpers/useAxios";

/**
 * Dependencies
 */
const builderLocalStorage = useBuilderLocalStorage();

/**
 * Form
 */
const formDestinationUrl = ref('');
const formParameters = ref<BuilderFormParameters>({
    utm_campaign: null,
    utm_content: null,
    utm_medium: null,
    utm_source: null,
    utm_term: null,
});

/**
 * Project related variables
 */
const project = ref<null | ProjectEntityInterface>(null);
const projectUrls = ref(reactive<BuilderUrlInterface[]>([]));
const projectParameters = ref<BuilderParametersByType>({
    utm_medium: [],
    utm_source: [],
    utm_term: [],
    utm_content: [],
    utm_campaign: []
});
const projectTemplates = ref(reactive<BuilderTemplateInterface[]>([]));
const projectBuilderSettings = ref<ProjectBuilderSettingsInterface>({
    lowercase: true,
    slugify: true,
    decode: true
});

const projectPermissions = ref({
    canManageTemplates: false,
    canManageShorturls: false,
})


/**
 * Maximizers
 */
const builderStats= ref<BuilderStats>({
    visits: 0
});

const maximizers = computed<BuilderMaximizers>(() => {

    const maximizers = {
        showSectionShare: false,
        showSectionCta: false,
        showSectionWarningStorage: false,
    }

    if(project.value) {
        // Do not show maximizers to registered users
        return maximizers;
    }

    // Simple
    return {
        showSectionShare: builderStats.value.visits > 2,
        showSectionCta: builderStats.value.visits <= 2,
        showSectionWarningStorage: builderStats.value.visits > 2,
    }
});

/**
 * Modal
 */
const showModalSettings = ref(false);
const showModalNeedToRegister = ref(false);

/**
 * URL Related Methods
 */
const saveUrlToDatabase: (destinationUrl: string, fullUrl: string) => Promise<BuilderUrlInterface> = async (destinationUrl, fullUrl) => {

    const parameters = parametersObjectToArray(formParameters.value);
    const utm_string = parameters.map(param => `${param.type}=${param.name}`).join('&');

    if (project.value === null) {

        const maxId = projectUrls.value.length > 0 ? Math.max.apply(Math, projectUrls.value.map(function (o) {
            return parseInt(o.id);
        })) : 0;

        const id = maxId + 1;

        const url: BuilderUrlInterface = {
            id: id.toString(),
            destination_url: destinationUrl,
            full_url: fullUrl,
            utm_string,
            parameters,
            shorturls: [],
        };

        return url;
    }

    const response = await useAxios().post<UrlEntityInterface>(
        route('app.project.urls.store', project.value.slug), {
            destination_url: destinationUrl,
            full_url: fullUrl,
            parameters
        });

    const url = response.data;

    const builderUrl: BuilderUrlInterface = {
        ...url,
        parameters,
        shorturls: []
    }

    return builderUrl;

}


/**
 * Functions
 * @param destinationUrl
 * @param fullUrl
 */
const saveUrl = async (destinationUrl: string, fullUrl: string) => {

    const url = await saveUrlToDatabase(destinationUrl, fullUrl);

    projectUrls.value.unshift(url);

    // Keep only 20 urls
    if (projectUrls.value.length > 20) {
        projectUrls.value.splice(20);
    }

    if (project.value === null) {
        builderLocalStorage.updateUrls(projectUrls.value);
    }

    return url;
};

const touchUrl = async (url: BuilderUrlInterface) => {

    let updatedUrl = url;

    if (project.value !== null) {
        const response = await useAxios().post<BuilderUrlInterface>(
            route('app.project.urls.touch', {
                    tool_project_entity: project.value.slug,
                    tool_url_entity: url.id
                }
            )
        );

        updatedUrl = response.data;
    }

    const existingIndex = projectUrls.value.findIndex(projectUrl => projectUrl.id === url.id);

    if(existingIndex < 0) {
        throw new Error('Could not find index of an url');
    } else if(existingIndex > -1) {
        projectUrls.value.splice(existingIndex, 1);
        projectUrls.value.unshift(updatedUrl);

        if (project.value === null) {
            builderLocalStorage.updateUrls(projectUrls.value);
        }

    }

}

/**
 * Shortening related methods
 */
const shortenUrl: (url: BuilderUrlInterface) => Promise<BuilderUrlInterface> = (async (url) => {

    if (project.value === null) {
        showModalNeedToRegister.value = true;
        throw new Error('Cannot shorten urls as a guest!');
    }

    const response = await useAxios().post<ShorturlEntityInterface>(
        route('app.project.urls.shorten', [project.value.slug, url.id])
    )

    const shorturl = response.data;

    // Update urls
    const urlIndex = projectUrls.value.findIndex(projectUrl => projectUrl.id === url.id);

    projectUrls.value[urlIndex].shorturls.push(shorturl);

    return url;
});


/**
 * Template related methods
 */
const saveTemplate: (name: string, formParameters: BuilderFormParameters, active: boolean, notes: string) => Promise<TemplateEntityInterface> = (
    async (name, formParameters, active, notes) => {
        if (project.value === null) {
            showModalNeedToRegister.value = true;
            throw new Error('Cannot save template as guest!')
        }

        const parameters = parametersObjectToArray(formParameters);

        const response = await useAxios().post<TemplateEntityInterface>(
            route('app.project.templates.store', project.value.slug), {
                name,
                active,
                notes,
                parameters,
            }
        )

        const template = response.data;

        projectTemplates.value.unshift(template);

        return template;
    });


/***
 * Parameters related methods
 */
const selectParameter = async (type: ParameterType, name: string | null) => {

    if (name === null) {
        formParameters.value[type] = null;
        return;
    }

    let parameter = projectParameters.value[type].find(param => name === param.name);

    if (!parameter) {
        // If parameter is not available, add it to the database in the background
        parameter = {
            name,
            type,
            notes: null
        }

        saveParameterToDatabase(type, name).then(() => {
        });
        // throw new Error(`Cannot select parameter that is not stored! Type: ${type}, Option: ${name}`);
    }

    formParameters.value[type] = parameter;
}


const saveParameterToDatabase: (type: ParameterType, name: string) => Promise<BuilderParameterInterface> = async (type, name) => {

    if (project.value === null) {

        const parameter: BuilderParameterInterface = {
            type,
            name,
            notes: null,
        };

        return parameter;
    }

    const response = await useAxios().post<ParameterEntityInterface>(
        route('app.project.parameters.store', project.value.slug), {
            type,
            name,
            active: true,
            findOrCreate: true,
        }
    )

    return response.data;
}

/**
 * @param type
 * @param name
 */
const addParameter = async (type: ParameterType, name: string) => {

    const parameter: BuilderParameterInterface = {
        name,
        type,
        notes: null
    }

    projectParameters.value[type].unshift(parameter);

    if (project.value === null) {
        builderLocalStorage.updateParameters(projectParameters.value);
    }

    formParameters.value[type] = parameter;

    // Save parameter to the database in the background
    saveParameterToDatabase(type, name).then(() => {
    });
}

/**
 * Null to clear all
 * @param types
 */
const clearParameters = (...types: ParameterType[]) => {
    const removeTypes = types.length === 0 ? Object.keys(PARAMETER_TYPES) as ParameterType[] : types;
    for (let i = 0; i < removeTypes.length; i++) {
        formParameters.value[removeTypes[i]] = null;
    }
}

const setParameters = (parameters: BuilderParameterInterface[], clearOtherParameters: boolean = true) => {
    if (clearOtherParameters) {
        clearParameters();
    }
    for (let i = 0; i < parameters.length; i++) {
        formParameters.value[parameters[i].type] = parameters[i];
    }
}

const parametersObjectToArray: (parametersByType: BuilderFormParameters) => BuilderParameterInterface[] = (parametersByType) => {
    return Object.values(parametersByType).filter(value => value !== null) as BuilderParameterInterface[];
};

/**
 * Settings related methods
 */
const saveBuilderSettings = async () => {
    if (project.value === null) {
        builderLocalStorage.updateBuilderSettings(projectBuilderSettings.value);
        return;
    }

    await useAxios().put<{ success: true }>(route('app.project.update-builder-settings', project.value.slug), projectBuilderSettings.value);
}


export default function useBuilderStore() {
    return {
        // Form
        formDestinationUrl,
        formParameters,

        // Project related data
        project,
        projectParameters,
        projectUrls,
        projectTemplates,
        projectBuilderSettings,
        projectPermissions,

        // Maximizers
        builderStats,
        maximizers,

        // Modal
        showModalSettings,
        showModalNeedToRegister,

        // Methods
        saveUrl,
        touchUrl,
        shortenUrl,
        saveTemplate,
        saveBuilderSettings,

        // Parameters
        selectParameter,
        addParameter,
        clearParameters,
        setParameters,
        parametersObjectToArray,

    }
}